import { useQuery } from "@tanstack/react-query";

import { useDataSource } from "@/adapters/data-source";
import { NOTIFICATIONS_KEY } from "@/components/notifications/queries";
import { ago } from "@/components/notifications/time";
import { LoadFailed } from "@/components/shell/EmptyState";
import { TableSkeleton } from "@/components/shell/LoadingState";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

const OVERVIEW_KEY = [...NOTIFICATIONS_KEY, "push", "overview"] as const;

function Figure({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-muted-foreground text-xs">{label}</dt>
      <dd className="text-lg font-semibold tabular-nums">{value}</dd>
    </div>
  );
}

/**
 * Whether phone and desktop push is set up on this environment, and what has
 * gone wrong with it (E6, #446, slice 5).
 *
 * Administrators only — the section needs `settings.company.manage` and the API
 * refuses anyone else. A push that could not be delivered is a row to read here,
 * not a line in a server log; it is named by the notification's stored sentence
 * ("You were assigned OPS-142"), which holds an identifier and nothing else.
 * Quiet hours, which also hold push, are set in the Notifications section.
 */
export function PushSettings() {
  const data = useDataSource();
  const query = useQuery({
    queryKey: OVERVIEW_KEY,
    queryFn: () => data.notifications.pushOverview(),
  });

  if (query.isPending) return <TableSkeleton rows={3} />;
  if (query.isError) {
    return (
      <LoadFailed
        message={query.error.message}
        onRetry={() => {
          void query.refetch();
        }}
      />
    );
  }
  const overview = query.data;

  return (
    <div className="space-y-4">
      <Card className="max-w-xl">
        <CardHeader>
          <CardTitle>Phone and desktop push</CardTitle>
          <CardDescription>
            {overview.configured
              ? "Push is set up on this environment. People turn it on for their own devices from Notification settings in their account menu."
              : "Push is not set up on this environment. Add the three VAPID variables to the API (VAPID_PUBLIC_KEY, VAPID_PRIVATE_KEY, VAPID_SUBJECT) and redeploy; until then nobody can turn it on."}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-3 gap-4">
            <Figure label="Devices" value={String(overview.deviceCount)} />
            <Figure label="People" value={String(overview.peopleCount)} />
            <Figure
              label="Sent, last 7 days"
              value={String(overview.sentLastWeek)}
            />
          </dl>
        </CardContent>
      </Card>

      <Card className="max-w-xl">
        <CardHeader>
          <CardTitle>Not delivered</CardTitle>
          <CardDescription>
            Pushes the push service refused after three tries, newest first.
            Pushes held by quiet hours are not here: they go out when quiet
            hours end.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {overview.problems.length === 0 ? (
            <p className="text-muted-foreground text-sm">
              Nothing has gone missing.
            </p>
          ) : (
            <ul className="divide-y">
              {overview.problems.map((problem) => (
                <li key={problem.id} className="space-y-0.5 py-3 text-sm">
                  <p className="font-medium">
                    {problem.text} for {problem.recipientName}
                  </p>
                  <p className="text-muted-foreground">
                    Failed {ago(problem.createdAt)}: {problem.reason}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
